import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/pages/ForgotPasswordPage.js - NEW: Complete forgot password flow
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  InputAdornment,
  Container,
  useTheme,
  Stepper,
  Step,
  StepLabel,
  Fade,
  CircularProgress,
  Paper
} from '@mui/material';
import {
  Email,
  ArrowBack,
  Send,
  CheckCircle as CheckIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import ArsenalLogo from '../components/common/ArsenalLogo';
import { api, handleApiError } from '../services/api';

// Validation schema
const validationSchema = Yup.object({
  email: Yup.string()
    .email(i18n.t('pages_forgotpasswordpage.validation.invalid_email'))
    .required(i18n.t('pages_forgotpasswordpage.validation.email_required')),
});

const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  // States
  const [currentStep, setCurrentStep] = useState(0); // 0: email, 1: confirmation, 2: success
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [userInfo, setUserInfo] = useState(null);

  const steps = [t('pages_forgotpasswordpage.step_email'), t('pages_forgotpasswordpage.step_confirm'), t('pages_forgotpasswordpage.step_completed')];

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      await handleEmailValidation(values.email);
    },
  });

  // Step 1: Validate email exists
  const handleEmailValidation = async (email) => {
    setIsLoading(true);
    setError('');

    try {
      const response = await api.auth.validateEmail({ email });
      
      if (response.data.success) {
        setUserInfo({
          email: email,
          name: response.data.user_name,
          companyCount: response.data.company_count
        });
        setCurrentStep(1);
      }
    } catch (err) {
      const errorInfo = handleApiError(err);
      setError(errorInfo.message || t('hardcoded.pages_forgotpasswordpage.email_no_encontrado_en_el'));
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Send password reset
  const handlePasswordReset = async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await api.auth.requestPasswordReset({ 
        email: userInfo.email 
      });
      
      if (response.data.success && response.data.email_sent) {
        setCurrentStep(2);
      } else {
        setError(response.data.message || t('hardcoded.pages_forgotpasswordpage.no_se_pudo_enviar_el'));
      }
    } catch (err) {
      const errorInfo = handleApiError(err);
      setError(errorInfo.message || t('hardcoded.pages_forgotpasswordpage.error_al_procesar_la_solicitud'));
    } finally {
      setIsLoading(false);
    }
  };

  const renderEmailStep = () => (
    <Box>
      <Typography
        variant="h4"
        component="h1"
        fontWeight={700}
        color="text.primary"
        gutterBottom
        textAlign="center"
      >
        {t('pages_forgotpasswordpage.title')}
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        textAlign="center"
        sx={{ mb: 4 }}
      >
        {t('pages_forgotpasswordpage.subtitle')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={formik.handleSubmit}>
        <TextField
          fullWidth
          id="email"
          name="email"
          label={t('hardcoded.pages_forgotpasswordpage.direccion_de_email')}
          type="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Email color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3 }}
          autoFocus
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={isLoading}
          sx={{
            py: 1.5,
            fontWeight: 600,
            fontSize: '1.1rem',
            textTransform: 'none',
            mb: 3,
          }}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              {t('pages_forgotpasswordpage.validating')}
            </>
          ) : (
            t('pages_forgotpasswordpage.validate_email')
          )}
        </Button>
      </Box>
    </Box>
  );

  const renderConfirmationStep = () => (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <CheckIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
        <Typography
          variant="h4"
          component="h1"
          fontWeight={700}
          color="text.primary"
          gutterBottom
        >
          {t('pages_forgotpasswordpage.email_confirmed')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('pages_forgotpasswordpage.verified_message')}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, bgcolor: 'grey.50', mb: 3 }}>
        <Typography variant="h6" gutterBottom color="primary">
          {t('pages_forgotpasswordpage.account_info')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>{t('pages_forgotpasswordpage.name_label')}</strong> {userInfo?.name}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>{t('pages_forgotpasswordpage.email_label')}</strong> {userInfo?.email}
        </Typography>
        <Typography variant="body2">
          <strong>{t('pages_forgotpasswordpage.companies_label')}</strong> {userInfo?.companyCount} {t('pages_forgotpasswordpage.companies_suffix')}
        </Typography>
      </Paper>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>{t('pages_forgotpasswordpage.what_happens')}</strong><br/>
          • {t('pages_forgotpasswordpage.bullet_new_password')}<br/>
          • {t('pages_forgotpasswordpage.bullet_receive_email')}<br/>
          • {t('pages_forgotpasswordpage.bullet_instant_login')}<br/>
          • {t('pages_forgotpasswordpage.bullet_change_password')}
        </Typography>
      </Alert>

      <Button
        fullWidth
        variant="contained"
        size="large"
        onClick={handlePasswordReset}
        disabled={isLoading}
        startIcon={isLoading ? <CircularProgress size={20} /> : <Send />}
        sx={{
          py: 1.5,
          fontWeight: 600,
          fontSize: '1.1rem',
          textTransform: 'none',
          mb: 2,
        }}
      >
        {isLoading ? t('pages_forgotpasswordpage.sending') : t('pages_forgotpasswordpage.send_new_password')}
      </Button>

      <Button
        fullWidth
        variant="outlined"
        onClick={() => setCurrentStep(0)}
        disabled={isLoading}
        startIcon={<ArrowBack />}
        sx={{ textTransform: 'none' }}
      >
        {t('pages_forgotpasswordpage.change_email')}
      </Button>
    </Box>
  );

  const renderSuccessStep = () => (
    <Box sx={{ textAlign: 'center' }}>
      <CheckIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
      
      <Typography
        variant="h4"
        component="h1"
        fontWeight={700}
        color="text.primary"
        gutterBottom
      >
        {t('pages_forgotpasswordpage.password_sent')}
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t('pages_forgotpasswordpage.sent_message')}<br/>
        <strong>{userInfo?.email}</strong>
      </Typography>

      <Alert severity="success" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>{t('pages_forgotpasswordpage.check_email_title')}</strong><br/>
          {t('pages_forgotpasswordpage.check_email_body')}
        </Typography>
      </Alert>

      <Paper sx={{ p: 3, bgcolor: 'grey.50', mb: 3 }}>
        <Typography variant="h6" gutterBottom color="primary">
          {t('pages_forgotpasswordpage.next_steps')}
        </Typography>
        <Typography variant="body2" component="div">
{t('pages_forgotpasswordpage.step_check_inbox')}<br/>
          {t('pages_forgotpasswordpage.step_copy_password')}<br/>
          {t('pages_forgotpasswordpage.step_login_with_temp')}<br/>
          {t('pages_forgotpasswordpage.step_change_it')}<br/>
          {t('pages_forgotpasswordpage.step_done')}
        </Typography>
      </Paper>

      <Button
        fullWidth
        variant="contained"
        size="large"
        onClick={() => navigate('/login')}
        sx={{
          py: 1.5,
          fontWeight: 600,
          fontSize: '1.1rem',
          textTransform: 'none',
          mb: 2,
        }}
      >
        {t('pages_forgotpasswordpage.go_to_login')}
      </Button>

      <Button
        fullWidth
        variant="outlined"
        onClick={() => {
          setCurrentStep(0);
          setUserInfo(null);
          setError('');
          formik.resetForm();
        }}
        sx={{ textTransform: 'none' }}
      >
        {t('pages_forgotpasswordpage.reset_another')}
      </Button>
    </Box>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return renderEmailStep();
      case 1:
        return renderConfirmationStep();
      case 2:
        return renderSuccessStep();
      default:
        return renderEmailStep();
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.primary.dark}25 100%)`,
        py: 3,
      }}
    >
      <Container maxWidth="sm">
        <Fade in timeout={500}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: theme.shadows[12],
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              {/* Header */}
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <ArsenalLogo
                  sx={{
                    width: 60,
                    height: 60,
                    color: theme.palette.primary.main,
                    mb: 2,
                  }}
                />
              </Box>

              {/* Progress Stepper */}
              <Stepper activeStep={currentStep} sx={{ mb: 4 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {/* Current Step Content */}
              {renderCurrentStep()}

              {/* Back to Login */}
              {currentStep === 0 && (
                <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <Link
                    component={RouterLink}
                    to="/login"
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    <ArrowBack sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    {t('pages_forgotpasswordpage.back_to_login')}
                  </Link>
                </Box>
              )}
            </CardContent>
          </Card>
        </Fade>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="caption" color="text.secondary">
            {t('pages_forgotpasswordpage.footer_copyright')}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ForgotPasswordPage;